'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds,
  type CalculatorConfig,
  type ControlValues,
  type NumberControl,
  type CalculatorControl,
  DataTableInputMode
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { BinomialCalculator, type BinomialResult } from './binomial-calculator';
import { calculateBinomialDistribution } from '../../../lib/stats';

interface BinomialControlValues extends ControlValues {
  [ControlIds.Trials]: number;
  [ControlIds.Successes]: number;
  [ControlIds.Probability]: number;
}

export const binomialControls = {
  tabs: [
    {
      title: 'Parameters',
      controls: [
        {
          id: ControlIds.Trials,
          type: ControlType.Number,
          label: 'Number of Trials (n)',
          required: true,
          min: 1,
          max: 100,
          step: 1,
          defaultValue: 20
        } satisfies NumberControl,
        {
          id: ControlIds.Successes,
          type: ControlType.Number,
          label: 'Number of Successes (k)',
          required: true,
          min: 0,
          step: 1,
          defaultValue: 10
        } satisfies NumberControl,
        {
          id: ControlIds.Probability,
          type: ControlType.Number,
          label: 'Probability of Success (p)',
          required: true,
          min: 0,
          max: 1,
          step: 0.01,
          defaultValue: 0.5
        } satisfies NumberControl
      ]
    }
  ]
} satisfies { tabs: { title: string; controls: readonly CalculatorControl[] }[] };

export const binomialConfig: CalculatorConfig<CalculatorId.BINOMIAL_DISTRIBUTION> = {
  id: CalculatorId.BINOMIAL_DISTRIBUTION,
  type: CalculatorType.Graph,
  name: 'Binomial Distribution Calculator',
  dataTableInputMode: DataTableInputMode.None,
  initialData: TableDataJSON.from({ columns: [] }), // No data needed for this calculator
  initialControlValues: {
    [ControlIds.Trials]: 20,
    [ControlIds.Successes]: 10,
    [ControlIds.Probability]: 0.5
  },
  controls: binomialControls,
  getInitialConfig: () => ({
    [ControlIds.Trials]: 20,
    [ControlIds.Successes]: 10,
    [ControlIds.Probability]: 0.5
  }),
  getVisualizationData: ({ controls }) => {
    const binomialControls = controls as BinomialControlValues;
    const { Trials, Successes, Probability } = ControlIds;
    
    const trials = binomialControls[Trials];
    const successes = binomialControls[Successes];
    const probability = binomialControls[Probability];

    // Validate inputs
    if (
      trials === undefined ||
      successes === undefined ||
      probability === undefined ||
      successes > trials
    ) {
      return null;
    }

    // Calculate binomial distribution
    const { pdf, cdf } = calculateBinomialDistribution({
      successes,
      trials,
      probability
    });

    // Generate full distribution for visualization
    const distribution: number[] = [];
    const successesArray: number[] = [];
    for (let k = 0; k <= trials; k++) {
      const { pdf } = calculateBinomialDistribution({
        successes: k,
        trials,
        probability
      });
      distribution.push(pdf);
      successesArray.push(k);
    }

    const result: BinomialResult = {
      pdf,
      cdf,
      distribution,
      successes: successesArray
    };
    
    return result;
  },
  render: ({ visualizationData }) => <BinomialCalculator visualizationData={visualizationData} />
}; 